/**
 * Seeded randomness. Every generated exercise draws from an `Rng` created from a
 * known seed, so a question can be reproduced exactly from
 * `(templateId, seed, difficulty)`. That makes tests stable, bug reports
 * actionable, and "retry this exact question" possible.
 *
 * Nothing in the engine may call `Math.random()`.
 */

/** FNV-1a, 32-bit. Stable across runs and platforms, unlike string hashing built into JS. */
export function hashString(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    // h *= 16777619, kept in 32-bit range via Math.imul
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Mixes two seeds into one. Order matters: combine(a, b) !== combine(b, a). */
export function hashCombine(a: number, b: number): number {
  let h = (a >>> 0) ^ Math.imul(b >>> 0, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 16), 0x21f0aaad);
  h = Math.imul(h ^ (h >>> 15), 0x735a2d97);
  return (h ^ (h >>> 15)) >>> 0;
}

export interface Weighted<T> {
  item: T;
  weight: number;
}

export class Rng {
  /** The seed this generator was created with, so a question can record it. */
  readonly seed: number;
  private state: number;

  constructor(seed: number) {
    this.seed = seed >>> 0;
    // A zero state makes mulberry32 degenerate, so nudge it off zero.
    this.state = this.seed === 0 ? 0x9e3779b9 : this.seed;
  }

  /** mulberry32: small, fast, and good enough for content generation. */
  float(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Integer in [minIncl, maxIncl]. Throws if the range is inverted. */
  int(minIncl: number, maxIncl: number): number {
    if (maxIncl < minIncl) {
      throw new Error(`Rng.int: empty range [${minIncl}, ${maxIncl}]`);
    }
    return minIncl + Math.floor(this.float() * (maxIncl - minIncl + 1));
  }

  chance(probability: number): boolean {
    return this.float() < probability;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick: empty array');
    return items[this.int(0, items.length - 1)] as T;
  }

  /** `n` distinct items. Throws rather than silently returning fewer. */
  pickN<T>(items: readonly T[], n: number): T[] {
    if (n > items.length) {
      throw new Error(`Rng.pickN: asked for ${n} of ${items.length}`);
    }
    return this.shuffle(items).slice(0, n);
  }

  /** Fisher-Yates. Returns a new array; the input is untouched. */
  shuffle<T>(items: readonly T[]): T[] {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i -= 1) {
      const j = this.int(0, i);
      const a = out[i] as T;
      out[i] = out[j] as T;
      out[j] = a;
    }
    return out;
  }

  /** Weighted pick. Non-positive weights are skipped. */
  weighted<T>(entries: readonly Weighted<T>[]): T {
    const usable = entries.filter((e) => e.weight > 0);
    if (usable.length === 0) throw new Error('Rng.weighted: no entry with positive weight');
    const total = usable.reduce((sum, e) => sum + e.weight, 0);
    let roll = this.float() * total;
    for (const entry of usable) {
      roll -= entry.weight;
      if (roll < 0) return entry.item;
    }
    return (usable[usable.length - 1] as Weighted<T>).item;
  }

  /**
   * A new generator derived from this seed and a label. Used to give each part
   * of a generator its own stream, so adding a draw in one place does not
   * shift every later value.
   */
  fork(label: string): Rng {
    return new Rng(hashCombine(this.seed, hashString(label)));
  }
}

/** Seed for one exercise slot inside a session. */
export function exerciseSeed(sessionSeed: number, slotIndex: number): number {
  return hashCombine(sessionSeed, slotIndex);
}

/** Seed for a session: stable per user, per day, per session ordinal. */
export function sessionSeed(userId: string, dayNumber: number, sessionCounter: number): number {
  return hashCombine(hashString(userId), hashCombine(dayNumber, sessionCounter));
}
